// printBuilders.js
//
// Pure functions: stageFields (+ lookup lists) -> plain data shapes for printing.
// No side effects, no axios, no state — safe to unit test on their own.
// Kept separate from the modal so the *data shape* survives a future swap
// from "browser print preview" to "real printer service" without touching
// RestaurantOrder.js call sites.

import { F } from './RestaurantOrderFields';

const nameFor = (id, list = [], labelKey = 'label', valueKey = 'value') => {
    const found = (list || []).find(x => x[valueKey] === id);
    return found ? found[labelKey] : id || '';
};

// ─────────────────────────────────────────────────────────────────────────
// KOT — grouped by kitchen, since that's how the physical tickets get routed.
// Only rows with MI_KOT_STATUS === 'Sent' (i.e. just sent this batch) are
// included by default; pass `allSent: true` to reprint everything already
// sent, not just the newest batch.
// ─────────────────────────────────────────────────────────────────────────
export function buildKotPrintData(stageFields, { menuItemsList = [], kitchenList = [], onlyPendingBatch = true } = {}) {
    const rows = stageFields[F.MENU_ITEMS]?.ContainerFields || [];
    const tableInfo = stageFields[F.TABLE_INFO]?.ContainerFields?.[0];
    const orderInfo = stageFields[F.ORDER_INFO]?.ContainerFields?.[0];

    const relevantRows = onlyPendingBatch
        ? rows.filter(r => r[F.MI_KOT_STATUS]?.FieldValue === 'Sent')
        : rows.filter(r => r[F.MI_KOT_STATUS]?.FieldValue && r[F.MI_KOT_STATUS]?.FieldValue !== 'Pending');

    const groups = {};
    relevantRows.forEach(row => {
        const kitchenId = row[F.MI_KITCHEN]?.FieldValue || 'unassigned';
        const kitchenName = kitchenId === 'unassigned' ? 'Kitchen' : nameFor(kitchenId, kitchenList);
        if (!groups[kitchenId]) groups[kitchenId] = { kitchenId, kitchenName, items: [] };

        groups[kitchenId].items.push({
            name: row.__displayName || nameFor(row[F.MI_ITEM]?.FieldValue, menuItemsList, 'menuName', 'menuItemId'),
            qty: Number(row[F.MI_QTY]?.FieldValue) || 0,
            instruction: row[F.MI_SPECIAL_INSTRUCTION]?.FieldValue || '',
        });
    });

    return {
        kotNumber: 'KOT-' + Date.now(),
        printedAt: new Date(),
        tableNumber: tableInfo?.[F.TABLE_NUMBER]?.FieldValue || null,
        orderNumber: stageFields[F.ORDER_NUMBER]?.FieldValue || 'New Order',
        waiter: orderInfo?.[F.WAITER]?.FieldValue || '',
        kitchenGroups: Object.values(groups),
    };
}

// ─────────────────────────────────────────────────────────────────────────
// BILL — flat line items + the summary block, ready to render top-to-bottom.
// ─────────────────────────────────────────────────────────────────────────
export function buildBillPrintData(stageFields, { menuItemsList = [], paymentTypeList = [], includeAllRows = false } = {}) {
    const rows = stageFields[F.MENU_ITEMS]?.ContainerFields || [];
    const billRow = stageFields[F.BILL_SUMMARY]?.ContainerFields?.[0] || {};
    const customerInfo = stageFields[F.CUSTOMER_INFO]?.ContainerFields?.[0];
    const tableInfo = stageFields[F.TABLE_INFO]?.ContainerFields?.[0];
    const paymentRows = stageFields[F.PAYMENT_INFO]?.ContainerFields || [];

    const billableRows = includeAllRows
        ? rows
        : rows.filter(r => r[F.MI_KOT_STATUS]?.FieldValue && r[F.MI_KOT_STATUS]?.FieldValue !== 'Pending');

    const lineItems = billableRows.map(row => {
        const qty = Number(row[F.MI_QTY]?.FieldValue) || 0;
        const rate = Number(row[F.MI_RATE]?.FieldValue) || 0;
        return {
            name: row.__displayName || nameFor(row[F.MI_ITEM]?.FieldValue, menuItemsList, 'menuName', 'menuItemId'),
            qty,
            rate,
            amount: qty * rate,
        };
    });

    const num = (v) => Number(v) || 0;
    const grandTotal = num(billRow[F.BILL_GRAND_TOTAL]?.FieldValue);

    // ── Bill status — derived from amounts, not the PAY_STATUS of any one row.
    // A single payment row's own status ('Paid') says nothing about whether it
    // covered the whole bill, so compare what was actually tendered (across
    // every real payment row, same as BillingScreen.getTotalPaid()) against
    // the grand total instead. ─────────────────────────────────────────────
    const realPaymentRows = paymentRows.filter(row => num(row[F.PAY_AMOUNT_TENDERED]?.FieldValue) > 0);
    const totalPaid = realPaymentRows.reduce((sum, row) => sum + num(row[F.PAY_AMOUNT_TENDERED]?.FieldValue), 0);
    const billStatus = totalPaid <= 0 ? 'Not Paid' : totalPaid < grandTotal ? 'Partially Paid' : 'Paid';
    const lastPaymentRow = realPaymentRows[realPaymentRows.length - 1];

    // ── Minimal per-payment breakdown — which method(s) covered how much of a
    // partially (or fully) paid bill, so that isn't just a single lump sum. ──
    const paymentEntries = realPaymentRows.map(row => ({
        method: nameFor(row[F.PAY_TYPE]?.FieldValue, paymentTypeList, 'label', 'value'),
        amount: num(row[F.PAY_AMOUNT_TENDERED]?.FieldValue),
        transactionNo: row[F.PAY_TRANSACTION_NO]?.FieldValue || '',
    }));

    return {
        billNumber: stageFields[F.ORDER_NUMBER]?.FieldValue || 'New Order',
        printedAt: new Date(),
        tableNumber: tableInfo?.[F.TABLE_NUMBER]?.FieldValue || null,
        customerName: customerInfo?.[F.CUST_NAME]?.FieldValue || '',
        customerMobile: customerInfo?.[F.CUST_MOBILE]?.FieldValue || '',
        lineItems,
        summary: {
            gross: num(billRow[F.BILL_GROSS]?.FieldValue),
            itemDiscount: num(billRow[F.BILL_ITEM_DISCOUNT]?.FieldValue),
            billDiscount: num(billRow[F.BILL_DISCOUNT]?.FieldValue),
            tax: num(billRow[F.BILL_TAX]?.FieldValue),
            serviceCharge: num(billRow[F.BILL_SERVICE_CHARGE]?.FieldValue),
            packingCharge: num(billRow[F.BILL_PACKING_CHARGE]?.FieldValue),
            deliveryCharge: num(billRow[F.BILL_DELIVERY_CHARGE]?.FieldValue),
            roundOff: num(billRow[F.BILL_ROUND_OFF]?.FieldValue),
            grandTotal,
        },
        billStatus,
        payment: realPaymentRows.length > 0 ? {
            status: billStatus,
            totalPaid,
            balance: Math.max(0, grandTotal - totalPaid),
            changeReturn: num(lastPaymentRow?.[F.PAY_CHANGE_RETURN]?.FieldValue),
            entries: paymentEntries,
        } : null,
    };
}
